import React, { useMemo } from 'react';

import { FormikHelpers } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { editStop, errorAlert, getStation, successAlert } from 'actions';
import { updateStopCodeApi } from 'app/operations/stations/station-api';
import {
  StopFormState,
  extractNumericCode,
} from 'app/operations/stations/stop-helpers';
import { useTypedSelector } from 'helpers';
import { getStationStop } from 'selectors';
import { Station, Stop } from 'types';

import { StopForm } from './stop-form';

const getLatLon = (point?: string) => {
  if (!point) return { lat: '', lon: '' };

  const [lon, lat] = point.replace('POINT (', '').replace(')', '').split(' ');

  return { lat, lon };
};

interface EditStopProps {
  setOpen: (newState: boolean) => void;
  stopId: string;
}

const EditStop: React.FC<EditStopProps> = ({ setOpen, stopId }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const match = useRouteMatch<{ id: string }>();

  const stationId = match.params.id;

  const stationData = useTypedSelector(
    state => state.stations.collection[stationId] as Station | undefined
  );

  const stop = useTypedSelector(
    state => getStationStop(state, { stationId, stopId }) as Stop | undefined
  );

  const initialState: StopFormState = useMemo(() => {
    const { lat, lon } = getLatLon(stop?.lonlat);

    return {
      ar_name: stop?.ar_name || '',
      en_name: stop?.en_name || '',
      lon,
      lat,
      stop_code: stop?.stop_code || '',
    };
  }, [stop]);

  const handleSubmit = async (
    values: StopFormState,
    { setSubmitting }: FormikHelpers<StopFormState>
  ) => {
    setSubmitting(true);
    try {
      const numericCode = extractNumericCode(values.stop_code);

      await Promise.resolve(
        dispatch(
          editStop(stopId, {
            en_name: values.en_name,
            ar_name: values.ar_name,
            lon: values.lon,
            lat: values.lat,
            code: numericCode,
            station_id: stationId,
            organisation_id: stationData?.organisation_id,
          })
        )
      );

      if (values.stop_code !== (stop?.stop_code || '')) {
        try {
          await updateStopCodeApi(stopId, values.stop_code);
        } catch (err: any) {
          const message =
            err?.response?.data?.message ||
            intl.formatMessage({ id: 'stopCodeUpdateFailed' });

          dispatch(errorAlert(message));

          return;
        }
      }

      setOpen(false);
      dispatch(getStation(stationId));
      dispatch(successAlert(intl.formatMessage({ id: 'successEditStop' })));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StopForm {...{ initialState, handleSubmit, setOpen, isEditForm: true }} />
  );
};

export { EditStop };
